import React, { useState, useEffect } from 'react';
import { naoPerturbeAPI, formatarErroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Download, Loader2, ArrowRightLeft, ExternalLink } from 'lucide-react';

const Passo = ({ n, children }) => (
  <li className="flex gap-2.5">
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold text-white">{n}</span>
    <span className="text-gray-600 dark:text-gray-300">{children}</span>
  </li>
);

export const ExtensaoRemanejar = () => {
  const [baixando, setBaixando] = useState(false);
  const [versao, setVersao] = useState(null);

  useEffect(() => {
    naoPerturbeAPI.extensaoRemanejarInfo().then((r) => setVersao(r.data?.versao)).catch(() => {});
  }, []);

  const baixar = async () => {
    setBaixando(true);
    try {
      const res = await naoPerturbeAPI.extensaoRemanejarDownload();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'remanejar-colaborador-claro.zip'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setBaixando(false);
    }
  };

  return (
    <Card data-testid="extensao-remanejar-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4" /> Extensão: Remanejar colaborador por ID da empresa
              {versao && <Badge variant="outline" className="ml-1 font-mono text-[11px]" data-testid="extensao-remanejar-versao">v{versao}</Badge>}
            </CardTitle>
            <CardDescription>Remaneja um colaborador para outro Agente Autorizado digitando o ID da empresa — mesmo que ela não apareça na lista do portal. Usa a sua sessão da Conexão Claro; não precisa parear com o CRM.</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.open('https://app.conexaoclarobrasil.com.br', '_blank', 'noopener')} data-testid="extensao-remanejar-portal-button">
            <ExternalLink className="w-4 h-4 mr-2" /> Abrir portal
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-lg border border-dashed p-3">
            <p className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> 1. Instalar (uma vez)</p>
            <ol className="space-y-1.5 text-xs">
              <Passo n="1">Baixe e <b>extraia o .zip</b> em uma pasta fixa (ex.: Documentos\remanejar-claro).</Passo>
              <Passo n="2">Abra <b>chrome://extensions</b> (Edge: edge://extensions) e ative <b>Modo do desenvolvedor</b>.</Passo>
              <Passo n="3"><b>Carregar sem compactação</b> → escolha a pasta extraída → fixe o ícone na barra.</Passo>
            </ol>
            <Button variant="outline" size="sm" onClick={baixar} disabled={baixando} className="mt-3 w-full" data-testid="extensao-remanejar-download-button">
              {baixando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Baixar extensão (.zip)
            </Button>
          </div>
          <div className="rounded-lg border border-dashed p-3">
            <p className="text-xs font-semibold mb-2 flex items-center gap-1.5"><ArrowRightLeft className="w-3.5 h-3.5" /> 2. Usar</p>
            <ol className="space-y-1.5 text-xs">
              <Passo n="1">Faça login no portal da Conexão Claro na mesma janela — o ícone fica <b>OK</b>.</Passo>
              <Passo n="2">Clique no ícone → busque por <b>nome, CPF ou IDs</b>; marque um ou vários (ou "selecionar todos"). <b>histórico</b> mostra o processo de cada um.</Passo>
              <Passo n="3">Digite o <b>ID da empresa de destino</b> (ou escolha da lista) → <b>Remanejar</b> / <b>Remanejar todos</b>. O resultado de cada colaborador aparece na hora.</Passo>
            </ol>
            <p className="text-[11px] text-gray-500 mt-2">Trocar de empresa sem permissão? Abra <b>"🔑 Bearer das empresas"</b> no topo da extensão e cole o Bearer da <b>empresa antiga</b> (para localizar) e da <b>empresa nova</b> (para remanejar).</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
